import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { createProspect, updateProspect, PROSPECT_STATUS } from "@/lib/prospectApi";
import { errMsg } from "@/lib/applyApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConvertSection } from "./ConvertSection";

const EMPTY = { contact_name: "", phone: "", email: "", horse_name: "", status: "new", follow_up_date: "", note: "" };
const fromP = (p) => (p ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] ?? EMPTY[k]])), note: "" } : EMPTY);
const when = (s) => new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function NotesLog({ p }) {
  if (!p?.notes?.length) return null;
  return (
    <div data-testid="prospect-notes-log" className="space-y-2">
      <Label className="text-sm">Notes history</Label>
      <ul className="max-h-48 overflow-y-auto space-y-2">
        {[...p.notes].reverse().map((n, i) => (
          <li key={i} data-testid={`prospect-note-${i}`} className="rounded-lg bg-stone-50 border border-stone-200 p-2.5 text-sm">
            <p className="whitespace-pre-wrap text-stone-800">{n.text}</p>
            <p className="mt-1 text-[11px] text-stone-500">{when(n.at)} · {n.by}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ProspectDialog({ open, prospect, onClose, onSaved, extra = {}, readOnly = false }) {
  const [v, setV] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setV(fromP(prospect)); } }, [open, prospect?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const p = prospect ? await updateProspect(prospect.id, v) : await createProspect({ ...v, ...extra });
      setV(fromP(p)); onSaved(p); toast.success(prospect ? "Prospect updated" : "Prospect added");
    } catch (e) { toast.error(errMsg(e, "Could not save prospect")); }
    finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <div className="space-y-1">
      <Label htmlFor={`pr-${k}`} className="text-sm">{label}</Label>
      <Input id={`pr-${k}`} data-testid={`prospect-${k.replace(/_/g, "-")}`} value={v[k]} onChange={set(k)} className="h-11 text-base sm:text-sm" {...props} />
    </div>
  );
  const opt = <span className="text-stone-400 font-normal">(optional)</span>;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="prospect-dialog" className="w-[calc(100%-1.5rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-xl">
        <DialogHeader>
          <DialogTitle>{prospect ? "Edit Prospect" : "Add Prospect"}</DialogTitle>
          <DialogDescription>Leads stay here, separate from horse ads, until you convert them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {field("contact_name", "Contact Name", { autoComplete: "name" })}
          <div className="grid sm:grid-cols-2 gap-4">
            {field("phone", <>Phone Number {opt}</>, { type: "tel", inputMode: "tel", autoComplete: "tel" })}
            {field("email", <>Email {opt}</>, { type: "email", inputMode: "email", autoComplete: "email" })}
          </div>
          {field("horse_name", <>Horse Name {opt}</>)}
          <div className="space-y-1.5">
            <Label className="text-sm">Status</Label>
            <div role="radiogroup" className="flex flex-wrap gap-2">
              {Object.entries(PROSPECT_STATUS).map(([k, [l]]) => (
                <button key={k} type="button" role="radio" aria-checked={v.status === k} data-testid={`prospect-status-option-${k}`} onClick={() => setV({ ...v, status: k })}
                  className={`h-11 px-3.5 rounded-full border text-sm font-semibold transition-colors duration-150 ${v.status === k ? "bg-amber-800 border-amber-800 text-white" : "bg-white border-stone-300 text-stone-700 hover:border-amber-700"}`}>{l}</button>
              ))}
            </div>
          </div>
          {field("follow_up_date", <>Follow-up Date {opt}</>, { type: "date" })}
          <div className="space-y-1">
            <Label htmlFor="pr-note" className="text-sm">{prospect ? "Add a note" : "Notes"} {opt}</Label>
            <Textarea id="pr-note" data-testid="prospect-note" rows={3} value={v.note} onChange={set("note")} className="text-base sm:text-sm" placeholder="Call details, what they're selling, timing…" />
          </div>
          <NotesLog p={prospect} />
          {prospect && !readOnly && <ConvertSection prospect={prospect} onConverted={onSaved} />}
        </div>
        <DialogFooter className="gap-2 sticky -bottom-6 bg-white pt-3 pb-1">
          <Button variant="outline" data-testid="prospect-close" onClick={onClose} className="h-11 rounded-full">Close</Button>
          <Button data-testid="prospect-save" onClick={save} disabled={readOnly || busy || !v.contact_name.trim()} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : prospect ? "Save Changes" : "Add Prospect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
